import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import {
  POLICY_CODE_LENGTH,
  padZeroToLeft,
} from '@/features/master-table/utils';
import { useInvalidateQuery } from '@/hooks';
import {
  extractLevelAndRemoveChildren,
  findUpperRecursive,
  genRecursiveOptions,
  getRecursiveArray,
} from '@/utils';
import type { z } from 'zod';
import {
  NestedPolicyOptionModel,
  PoliciesWithOrganizationNameModel,
  PolicyFormSchema,
  PolicyModel,
  PolicyWithOrganizationNameModel,
  UpdatePolicyParamsSchema,
} from '../_models/policy';
import {
  policyInvalidateListMap,
  policyQueryKeyMap,
} from '../_utils/query-keys';

const BASE_PATH = '/api/policies/';

export const usePolicies = <
  T = Array<z.infer<typeof PolicyWithOrganizationNameModel>>,
  U = T,
>({
  params,
  select,
  enabled,
}: {
  params?: { referenceFiscalYear?: string; isMigrated?: boolean };
  select?: (data: z.infer<typeof PoliciesWithOrganizationNameModel>) => U;
  enabled?: boolean;
} = {}) => {
  return useQuery({
    queryKey: policyQueryKeyMap.policies(params),
    queryFn: async () => {
      const policies = await axiosGet(BASE_PATH, params);
      return PoliciesWithOrganizationNameModel.parse(policies);
    },
    select,
    enabled,
  });
};

export const usePolicy = (id: string) => {
  return useQuery({
    queryKey: policyQueryKeyMap.policy(id),
    queryFn: async () => {
      const policy = await axiosGet(`${BASE_PATH}${id}/`);
      return PolicyWithOrganizationNameModel.parse(policy);
    },
    enabled: !!id,
  });
};

export const usePolicyRecursive = (id: string, isMigrated?: boolean) => {
  return usePolicies({
    params: { isMigrated },
    select: (data) => findUpperRecursive(id, data, []).reverse(),
    enabled: !!id,
  });
};

export const usePolicyOptions = ({
  upperLevel,
  showCode,
  params,
  enabled,
}: {
  upperLevel?: number;
  showCode?: boolean;
  params?: { referenceFiscalYear?: string; isMigrated?: boolean };
  enabled?: boolean;
} = {}) => {
  const options = usePolicies({
    params,
    select: (data) => {
      const recursiveArray = extractLevelAndRemoveChildren(
        getRecursiveArray(
          data.filter((d) => !d.upperId),
          data.filter((d) => !upperLevel || d.level <= upperLevel),
        ),
        upperLevel,
      );
      return genRecursiveOptions(
        showCode
          ? padZeroToLeft(recursiveArray, POLICY_CODE_LENGTH)
          : recursiveArray,
        'id',
        'name',
      );
    },
    enabled,
  });
  return options;
};

type FormValues = z.infer<typeof PolicyFormSchema>;

export const useCreatePolicy = () => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async (values: FormValues) => {
      const { level, upperIds, organizationIds, period, ...payload } =
        PolicyFormSchema.parse(values);
      const res = await axiosPost(BASE_PATH, {
        ...payload,
        upperId: upperIds?.at(-1),
        organizationId: organizationIds?.at(-1),
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });

      return PolicyModel.parse(res);
    },
    onSuccess: async () => {
      await Promise.all(
        policyInvalidateListMap.update().map((key) => invalidate(key)),
      );
    },
  });
};

export const useUpdatePolicy = () => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async (values: FormValues & { id: string }) => {
      const { id, level, upperIds, organizationIds, period, ...payload } =
        UpdatePolicyParamsSchema.parse(values);
      const res = await axiosPatch(`${BASE_PATH}${id}/`, {
        ...payload,
        upperId: upperIds?.at(-1),
        organizationId: organizationIds?.at(-1),
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });

      return PolicyModel.parse(res);
    },
    onSuccess: async (_, { id }) => {
      await Promise.all(
        policyInvalidateListMap.update(id).map((key) => invalidate(key)),
      );
    },
  });
};

export const useDeletePolicy = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      return await axiosDelete(`${BASE_PATH}${id}/`);
    },
    onSuccess: async (_, id) => {
      await Promise.all(
        policyInvalidateListMap.update(id).map((key) => invalidate(key)),
      );
    },
  });
};

type CascaderOption = {
  label: string;
  value: string | number;
  children?: CascaderOption[] | null;
  disabled?: boolean;
};

const convertToCascaderOptions = (
  data: z.infer<typeof NestedPolicyOptionModel>,
): CascaderOption[] => {
  return data.map((item) => ({
    label: item.name,
    value: item.id,
    children: item.children ? convertToCascaderOptions(item.children) : null,
  }));
};

export const usePoliciesOptions = ({
  params,
}: {
  params?: { referenceFiscalYear?: string };
} = {}) => {
  return useQuery({
    queryKey: policyQueryKeyMap.policiesOptions(params),
    queryFn: async () => {
      const res = await axiosGet('/api/policies/nested-options/', params);
      const data = NestedPolicyOptionModel.parse(res);
      return convertToCascaderOptions(data);
    },
    cacheTime: 60 * 60 * 12 * 1000,
    staleTime: 60 * 60 * 12 * 1000,
  });
};
